// src/pages/freevaluation/FreeValuationLanding.jsx
//
// Public marketing landing page for the "Få en kostnadsfri värdering"
// funnel. Collects the seller's email → triggers a magic-link send →
// shows a "check your inbox" state. No account creation, no password.
//
// Route: /gratis-vardering (public, no auth required).
// Backend: POST /valuation/free-valuation/request-link/ (from
// backend/valuation/free_valuation_views.py::request_magic_link).

import React, { useState } from "react";
import { request } from "../../services/server";
import "./freevaluation.css";

function _readUtmFromLocation() {
  // Marketing attribution — utm_source, utm_medium, utm_campaign,
  // utm_term, utm_content. Read from the URL query string once at
  // mount time; the token endpoint stores whatever we pass in
  // ``context`` and re-attaches it to the workspace on successful
  // submit downstream.
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  for (const key of [
    "utm_source", "utm_medium", "utm_campaign",
    "utm_term", "utm_content", "referrer",
  ]) {
    const v = params.get(key);
    if (v) utm[key] = v;
  }
  if (document.referrer && !utm.referrer) {
    utm.referrer = document.referrer;
  }
  return utm;
}

export default function FreeValuationLanding() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError("Ange en giltig e-postadress.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      const utm = _readUtmFromLocation();
      await request("/valuation/free-valuation/request-link/", {
        method: "POST",
        body: { email, utm },
      });
      setStatus("sent");
    } catch (err) {
      setError(
        err?.message ||
          "Något gick fel när mailet skulle skickas. Försök igen om en stund."
      );
      setStatus("idle");
    }
  };

  if (status === "sent") {
    return (
      <div className="fv-page">
        <div className="fv-card">
          <h1>Kolla din inkorg</h1>
          <p>
            Vi har skickat en inloggningslänk till <strong>{email}</strong>.
            Klicka på länken för att gå vidare med värderingen. Länken
            gäller i 30 minuter.
          </p>
          <p className="fv-muted">
            Inget mail? Kolla skräpposten eller{" "}
            <button
              type="button"
              className="fv-linkbutton"
              onClick={() => setStatus("idle")}
            >
              försök med en annan adress
            </button>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fv-page">
      <div className="fv-card">
        <h1>Få en kostnadsfri värdering på 60 sekunder</h1>
        <p className="fv-lead">
          Ladda upp en SIE-fil från ditt bokföringssystem och få ett
          uppskattat värdeintervall för ditt bolag. Ingen registrering,
          ingen kreditkortsuppgift.
        </p>

        <form onSubmit={onSubmit} className="fv-form">
          <label className="fv-label">
            Din e-postadress
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@bolag.se"
              autoComplete="email"
              disabled={status === "sending"}
            />
          </label>
          <button
            type="submit"
            className="fv-primary"
            disabled={status === "sending"}
          >
            {status === "sending" ? "Skickar…" : "Få min värdering"}
          </button>
          {error && <p className="fv-error">{error}</p>}
        </form>

        <ul className="fv-checklist">
          <li>Ingen registrering krävs</li>
          <li>Data behandlas konfidentiellt</li>
          <li>Baserad på din faktiska bokföring</li>
        </ul>
      </div>
    </div>
  );
}
